import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import i18n from 'i18next';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import truncate from 'lodash/truncate';

import {
  AppHeader,
  getMenuItems,
  getIsCoursePublishing,
  history,
} from '@/exceed';

import { useUrlSearch } from '@/search';

import getDeleteItemModal from '../helpers/getDeleteItemModal';
import getTopicPropertiesBreadcrumbs from '../helpers/getTopicPropertiesBreadcrumbs';
import handleAdminVideoAxiosError from '../helpers/handleAdminVideoAxiosError';

import useTopicProperties from '../hooks/useTopicProperties';
import useVideosAdminTree from '../hooks/useVideosAdminTree';
import useVideoVersion from '../hooks/useVideoVersion';

import VideoShortcutTag from '../components/videoShortcutTag';
import VideoVersioningToolbar from '../components/videoVersioningToolbar';

import styles from '../styles/videoAdminHeader.module.scss';

const VideosAdminHeaderContainer = ({ isDetailsPage = false, isTopicsPage = false }) => {
  const params = useParams();
  const { id: contentId, orgId, tabId, type } = params;
  const isInLiveMode = type === 'live';
  const rootUrl = `/videos/organizations/${orgId}/${type}`;
  const { flattenedVideosAdminTree } = useVideosAdminTree({ isTopicsPage });
  const { topicProperties, topicPropertiesKey } = useTopicProperties({
    isEnabled: isDetailsPage && isTopicsPage,
  });
  const { videoVersion, videoVersionKey } = useVideoVersion({
    isEnabled: false,
  });
  const urlSearch = useUrlSearch();

  const handleDeleteItem = async ({ contentType }) => {
    const apiType = isInLiveMode ? 'live_videos' : 'videos';
    const versionId =
      contentType === 'video' ? videoVersion.data?.draft_id : topicProperties.data?.topic_id;
    const url =
      contentType === 'video'
        ? `/api/admin/${apiType}/${contentId}${versionId ? `?version_id=${versionId}` : ''}`
        : `/api/admin/videos/topics/${contentId}${versionId ? `?version_id=${versionId}` : ''}`;

    try {
      await axios.delete(url);
    } catch (error) {
      return handleAdminVideoAxiosError(error);
    }

    if ((contentType === 'video' ? videoVersion : topicProperties).data?.is_child_locale) {
      urlSearch.deleteValue('version_id');
    } else {
      history.push(rootUrl);
    }
  };

  const getVideosComposeKebabOptions = () => {
    if (!isDetailsPage) return;

    const isDisabled = getIsCoursePublishing(videoVersion);

    return getMenuItems([
      {
        icon: 'newWindow',
        key: 'launch-published',
        isDisabled: isDisabled || !videoVersion.data.is_published,
        onClick: () =>
          window.open(
            isInLiveMode
              ? `/student/videos/live/${videoVersion.data.video_id}`
              : `/student/videos/on-demand/${contentId}`,
            '_blank',
          ),
      },
      {
        icon: 'preview',
        key: 'preview-changes',
        onClick: () =>
          window.open(
            isInLiveMode
              ? `/student/videos/live/${videoVersion.data.draft_id}`
              : `/student/videos/on-demand/${contentId}?version_id=${videoVersion.data.draft_id}`,
            '_blank',
          ),
      },
      {
        icon: 'copy',
        key: 'duplicate',
        onClick: () => {},
        isDisabled: true,
      },
      {
        icon: 'deleteOutline',
        key: 'delete',
        isDisabled,
        onClick: () => {
          const isChildLocale = videoVersion.data?.is_child_locale;
          getDeleteItemModal({
            title: i18n.t(`delete-video${isChildLocale ? '-locale' : ''}`),
            body: i18n.t(`delete-video${isChildLocale ? '-locale' : ''}-help`),
            onDeleteButtonClick: () => handleDeleteItem({ contentType: 'video' }),
          });
        },
      },
    ]);
  };

  const getTopicPropertiesKebabOptions = () =>
    getMenuItems([
      {
        icon: 'newWindow',
        key: 'launch-published',
        onClick: () => window.open(`/student/videos/topics/${contentId}`, '_blank'),
      },
      {
        icon: 'deleteOutline',
        key: 'delete',
        onClick: () => {
          const isChildLocale = topicProperties.data?.is_child_locale;
          getDeleteItemModal({
            title: i18n.t(`delete-video-topic${isChildLocale ? '-locale' : ''}`),
            body: i18n.t(`delete-video-topic${isChildLocale ? '-locale' : ''}-help`),
            onDeleteButtonClick: () => handleDeleteItem({ contentType: 'topic' }),
          });
        },
      },
    ]);

  const getTopicPermissionsTitle = () => {
    if (flattenedVideosAdminTree.status !== 'success') return;

    return flattenedVideosAdminTree.data.find(({ id }) => id === Number(contentId))?.name;
  };

  const getTitle = () => {
    if (!isDetailsPage) return i18n.t('videos');

    const isTopicPropertiesPage = isTopicsPage && tabId === 'properties';
    const isTopicsPermissionsPage = tabId === 'topic-permissions';

    if (isTopicPropertiesPage) return topicProperties?.data?.title;

    if (isTopicsPermissionsPage) return getTopicPermissionsTitle();

    if (videoVersion.status === 'success') return videoVersion.data.name;

    return i18n.t('videos');
  };

  const getVideoBreadcrumbs = (title) => {
    const list = [
      {
        text: i18n.t('videos'),
        path: rootUrl,
        key: 'videos-home-page',
      },
    ];

    if (videoVersion.status === 'success') {
      videoVersion.data.breadcrumbs?.forEach((breadcrumb) => {
        list.push({
          text: truncate(breadcrumb.name, { length: 50 }),
          path: `${rootUrl}/topics/${breadcrumb.id}`,
          key: `breadcrumb-topic-${breadcrumb.id}`,
        });
      });
    }

    if (title) {
      list.push({
        text: truncate(title, { length: 50 }),
        path: '',
        key: `breadcrumb-video-${contentId}`,
      });
    }

    return list;
  };

  const { is_shortcut: isShortcut, state: versionState } = videoVersion.data || {};
  const isShortcutPage = isDetailsPage && isShortcut;
  const title = getTitle();
  const kebabMenuItems = isTopicsPage
    ? getTopicPropertiesKebabOptions()
    : getVideosComposeKebabOptions();

  const breadcrumbs =
    isDetailsPage && !isTopicsPage
      ? getVideoBreadcrumbs(title)
      : getTopicPropertiesBreadcrumbs({
          params,
          topicTitle: title,
          flattenedVideosAdminTree,
        });

  return (
    <div className={classnames(!isDetailsPage && styles.hasMarginTop)}>
      {isDetailsPage && !isShortcutPage && (
        <VideoVersioningToolbar
          cache={isTopicsPage ? topicProperties : videoVersion}
          cacheKey={isTopicsPage ? topicPropertiesKey : videoVersionKey}
          cacheType={isTopicsPage ? 'topic' : 'video'}
        />
      )}
      <AppHeader
        className={styles.header}
        hasLabel={isShortcutPage}
        isLocked={isDetailsPage && versionState && versionState !== 'editing'}
        label={<VideoShortcutTag />}
        lockType={versionState}
        title={title}
        hasBreadcrumbs={isDetailsPage}
        breadcrumbs={breadcrumbs}
        hasKebabMenu={isDetailsPage}
        kebabMenuItems={kebabMenuItems}
      />
    </div>
  );
};

VideosAdminHeaderContainer.propTypes = {
  isDetailsPage: PropTypes.bool,
  isTopicsPage: PropTypes.bool,
};

export default VideosAdminHeaderContainer;
